'use client';

import { useId } from 'react';
import { ArrowLeft, Lightbulb, MessageCircle, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader } from '@/components/ui/loader';
import { Textarea } from '@/components/ui/textarea';
import type { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { cn } from '@/lib/utils';

import {
  getPostHogSupportContentWidth,
  getPostHogSupportHorizontalPadding,
} from './posthog-support-layout';
import type {
  SupportIntent,
  SupportPageContext,
} from './posthog-support-state';
import { canSendSupportMessage } from './posthog-support-state';

type SupportCopy = ReturnType<typeof getPostHogSupportCopy>;

type PostHogSupportComposerProps = {
  compact: boolean;
  copy: SupportCopy;
  blockingWork: boolean;
  context: SupportPageContext;
  error: string | null;
  intent: SupportIntent;
  isSending: boolean;
  message: string;
  onBack: () => void;
  onBlockingWorkChange: (blocking: boolean) => void;
  onMessageChange: (message: string) => void;
  onSubmit: () => void | Promise<void>;
};

function getIntentCopy(copy: SupportCopy, intent: SupportIntent) {
  if (intent === 'problem') {
    return {
      label: copy.reportProblem,
      placeholder: copy.whatHappened,
      sendLabel: copy.sendReport,
    };
  }
  if (intent === 'question') {
    return {
      label: copy.askTeam,
      placeholder: copy.questionPlaceholder,
      sendLabel: copy.sendQuestion,
    };
  }
  return {
    label: copy.shareIdea,
    placeholder: copy.ideaPlaceholder,
    sendLabel: copy.sendIdea,
  };
}

export function PostHogSupportComposer({
  blockingWork,
  compact,
  copy,
  context,
  error,
  intent,
  isSending,
  message,
  onBack,
  onBlockingWorkChange,
  onMessageChange,
  onSubmit,
}: PostHogSupportComposerProps) {
  const intentCopy = getIntentCopy(copy, intent);
  const hasContext = Boolean(context.page || context.workspace);
  const inputId = useId();
  const contextId = useId();
  const errorId = useId();

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className={cn(
          'shrink-0 pb-2',
          getPostHogSupportHorizontalPadding(compact)
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2',
            getPostHogSupportContentWidth(compact)
          )}
        >
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="-ms-2 size-11 rounded-full sm:size-8"
            onClick={onBack}
            aria-label={copy.back}
          >
            <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden="true" />
          </Button>
          <h2
            className="text-sm font-semibold text-balance"
            data-support-view-heading
            tabIndex={-1}
          >
            {intentCopy.label}
          </h2>
        </div>
      </div>

      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
      >
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto py-2',
            getPostHogSupportHorizontalPadding(compact)
          )}
        >
          <div
            className={cn('space-y-4', getPostHogSupportContentWidth(compact))}
          >
            <div>
              <label
                htmlFor={inputId}
                className="mb-2 block text-sm font-medium"
              >
                {copy.messageLabel}
              </label>
              <Textarea
                id={inputId}
                value={message}
                onChange={(event) => onMessageChange(event.target.value)}
                placeholder={intentCopy.placeholder}
                disabled={isSending}
                aria-invalid={Boolean(error)}
                aria-describedby={
                  [hasContext ? contextId : null, error ? errorId : null]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                className="border-border/35 focus-visible:border-border/55 min-h-36 resize-none px-4 py-3 text-base"
              />
            </div>

            {intent === 'problem' ? (
              <label className="flex min-h-11 cursor-pointer items-center gap-2 px-1 text-sm">
                <Checkbox
                  checked={blockingWork}
                  onCheckedChange={(checked) =>
                    onBlockingWorkChange(checked === true)
                  }
                  disabled={isSending}
                />
                <span>{copy.blockingWork}</span>
              </label>
            ) : null}

            {hasContext ? (
              <section
                id={contextId}
                className="bg-muted/35 rounded-lg px-3 py-3"
              >
                <div className="flex items-start gap-2">
                  <ShieldCheck
                    className="text-muted-foreground mt-0.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="text-xs font-medium text-balance">
                      {copy.includedDetails}
                    </h3>
                    <p className="text-muted-foreground mt-0.5 text-xs leading-5 text-pretty">
                      {copy.contextDescription}
                    </p>
                    <dl className="mt-2 space-y-1 text-xs">
                      {context.page ? (
                        <div className="flex min-w-0 gap-2">
                          <dt className="text-muted-foreground shrink-0">
                            {copy.pageLabel}
                          </dt>
                          <dd className="min-w-0 truncate" dir="ltr">
                            {context.page}
                          </dd>
                        </div>
                      ) : null}
                      {context.workspace ? (
                        <div className="flex min-w-0 gap-2">
                          <dt className="text-muted-foreground shrink-0">
                            {copy.workspaceLabel}
                          </dt>
                          <dd className="min-w-0 truncate" dir="auto">
                            {context.workspace}
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                    <p className="text-muted-foreground mt-2 text-xs leading-4 text-pretty">
                      {copy.privacyDescription}
                    </p>
                  </div>
                </div>
              </section>
            ) : null}

            <div className="text-muted-foreground flex items-start gap-2 px-1 text-xs leading-5">
              {intent === 'idea' ? (
                <Lightbulb className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              ) : (
                <MessageCircle
                  className="mt-0.5 size-3.5 shrink-0"
                  aria-hidden
                />
              )}
              <a
                href="mailto:contact@imai.studio"
                className="focus-visible:ring-ring rounded-sm text-pretty underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
              >
                {copy.attachmentUnsupported}
              </a>
            </div>
          </div>
        </div>

        <div
          className={cn(
            'border-border/35 bg-background shrink-0 border-t pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]',
            getPostHogSupportHorizontalPadding(compact)
          )}
        >
          <div className={getPostHogSupportContentWidth(compact)}>
            {error ? (
              <p
                id={errorId}
                role="alert"
                className="text-destructive mb-2 text-xs text-pretty"
              >
                {error}
              </p>
            ) : null}
            <div className="flex items-center justify-end">
              <Button
                type="submit"
                size="sm"
                disabled={!canSendSupportMessage({ message, isSending })}
                className="min-h-11 shrink-0 sm:min-h-8"
              >
                {isSending ? (
                  <>
                    <span aria-hidden="true">
                      <Loader />
                    </span>
                    {copy.sending}
                  </>
                ) : (
                  intentCopy.sendLabel
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
